/* ==========================================================================
   dg-home.js — behaviour for the Dolphin Galleries home page (sections/dg-home.liquid)

   Five small jobs, all of them progressive: the page is complete and navigable with this
   file blocked. Nothing here writes content, only measures, reveals, plays and scrolls it.

     1. Header    — measures the theme header so the hero can be "one screen including
                    the header". CSS ships a correct fallback for both breakpoints, so
                    this only corrects for a header that is taller than expected.
     2. Reveal    — fades sections up as they arrive, staggering siblings. The opacity:0
                    start state is applied by adding .dg-anim here, so a failure leaves
                    everything visible rather than a blank page.
     3. Films     — the vertical panels carry an empty src; the source is attached and
                    playback started only when the panel reaches the viewport, and paused
                    the moment it leaves. The HERO is the exception: it has a real src and
                    the autoplay attribute in the markup, so it starts with this file
                    blocked, and what is left here is the pause-when-scrolled-past, the
                    poster fade and a retry for a phone that refuses the first attempt.
     4. Works     — a product tile whose gallery holds a video swaps to it on hover, same
                    as the collection grid. Where there is no hover to give it plays on
                    view instead, because otherwise a phone never sees these films at all.
     5. Rails     — arrow buttons for the horizontal work rails, disabled at each end.

   prefers-reduced-motion is honoured everywhere: no reveals, no autoplay, no hover swap.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.querySelector('[data-dg-home]');
  if (!root) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var hasIO = 'IntersectionObserver' in window;

  /* --- 1. header height ------------------------------------------------- */

  function measureHeader() {
    var header = document.querySelector('.site-header') ||
                 document.querySelector('#shopify-section-header') ||
                 document.querySelector('header');
    if (!header) return;

    var h = Math.round(header.getBoundingClientRect().height);
    // A sticky or collapsed header can measure as 0 mid-scroll; the CSS fallback is a
    // better answer than a hero that suddenly grows by 150px.
    if (h > 0 && h < window.innerHeight * 0.5) {
      root.style.setProperty('--dgh-header', h + 'px');
    }
  }

  measureHeader();
  window.addEventListener('load', measureHeader);
  window.addEventListener('resize', measureHeader);

  /* --- 2. reveal --------------------------------------------------------- */

  var STAGGER_MS = 70;
  var STAGGER_CAP = 8; // past eight, extra delay is just waiting

  function initReveal() {
    if (!hasIO || reduceMotion.matches) return;

    // Children of a staggered container get their own delay, and the container stops
    // being a reveal target itself.
    var groups = root.querySelectorAll('[data-reveal-stagger]');
    for (var g = 0; g < groups.length; g++) {
      var kids = groups[g].children;
      for (var k = 0; k < kids.length; k++) {
        kids[k].setAttribute('data-reveal-item', '');
        kids[k].style.setProperty(
          '--dgh-delay', (Math.min(k, STAGGER_CAP) * STAGGER_MS) + 'ms');
      }
    }

    var targets = root.querySelectorAll('[data-reveal], [data-reveal-item]');
    if (!targets.length) return;

    root.classList.add('dg-anim');

    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        entries[i].target.classList.add('is-in');
        io.unobserve(entries[i].target);
      }
    }, {
      // Start a little before the element's top edge clears the fold, so the motion is
      // finishing as it becomes properly readable rather than starting then.
      rootMargin: '0px 0px -6% 0px',
      threshold: 0.02
    });

    for (var t = 0; t < targets.length; t++) {
      // The hero is already on screen; it plays on the next frame instead of waiting for
      // an intersection callback that would land after first paint.
      if (targets[t].closest('.dgh-hero')) continue;
      io.observe(targets[t]);
    }

    var heroItems = root.querySelectorAll('.dgh-hero [data-reveal-item], .dgh-hero [data-reveal]');
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        for (var h = 0; h < heroItems.length; h++) heroItems[h].classList.add('is-in');
      });
    });
  }

  /* --- shared video helpers --------------------------------------------- */

  // The src lives in data-film-src until first use; assigning it is what starts the
  // download, so a film that is never reached is never fetched.
  function attach(video) {
    if (!video || video.getAttribute('src')) return;
    var src = video.getAttribute('data-film-src');
    if (src) video.setAttribute('src', src);
  }

  // A rejected play() is a policy refusal, not an error: iOS in Low Power Mode turns down
  // even a muted inline film, and so does Data Saver on Android. Swallowing the rejection
  // leaves the poster up for the rest of the visit. The one thing that lifts the refusal
  // is a user gesture, so anything refused is parked and tried again on the first touch
  // or click anywhere on the page.
  var refused = [];

  function playRefused() {
    var list = refused.splice(0, refused.length);
    for (var i = 0; i < list.length; i++) {
      var attempt = list[i].play();
      if (attempt && attempt.catch) attempt.catch(function () {});
    }
  }

  function play(video) {
    if (!video || reduceMotion.matches) return;
    // The attribute is not always enough on WebKit once a source has been assigned after
    // parse — the autoplay policy reads the property. Setting it costs nothing.
    video.muted = true;
    var attempt = video.play();
    if (!attempt || !attempt.catch) return;
    attempt.catch(function () {
      // The other reason play() rejects is that it was asked before there was anything to
      // play: these panels get their src the instant they are wanted, so the request can
      // land on an empty buffer. That one clears itself the moment data arrives and needs
      // no gesture, so try it again then as well as on the next touch.
      video.addEventListener('canplay', function once () {
        video.removeEventListener('canplay', once);
        var retry = video.play();
        if (retry && retry.catch) retry.catch(function () {});
      });

      if (refused.indexOf(video) !== -1) return;
      if (!refused.length) {
        document.addEventListener('touchstart', playRefused, { passive: true });
        document.addEventListener('click', playRefused);
      }
      refused.push(video);
    });
  }

  // The poster is lifted on the film's own `playing` event rather than in the callback
  // that asked for playback: a poster sitting over a running film and a poster cleared
  // off a film that never started are both worse than having no poster at all.
  function trackPlayState(panel) {
    var video = panel.querySelector('video');
    if (!video) return;
    video.addEventListener('playing', function () { panel.classList.add('is-playing'); });
    video.addEventListener('pause', function () { panel.classList.remove('is-playing'); });
    if (!video.paused && video.readyState > 2) panel.classList.add('is-playing');
  }

  /* --- 3. hero film and panel films -------------------------------------- */

  function initFilms() {
    var panels = root.querySelectorAll('[data-film]');
    if (!panels.length) return;

    // Posters stand in, which is the point of them. The hero carries the autoplay
    // attribute now, so under reduced motion that has to be actively undone rather than
    // merely left alone — this file is deferred, so it lands within a frame or two.
    if (reduceMotion.matches) {
      for (var r = 0; r < panels.length; r++) {
        var stop = panels[r].querySelector('video');
        if (!stop) continue;
        stop.autoplay = false;
        stop.pause();
        panels[r].classList.remove('is-playing');
      }
      return;
    }

    for (var t = 0; t < panels.length; t++) trackPlayState(panels[t]);

    // No observer is not a reason for the hero to sit on its poster: it is on screen by
    // definition. Start everything and let it run.
    if (!hasIO) {
      for (var f = 0; f < panels.length; f++) {
        var eager = panels[f].querySelector('video');
        if (!eager) continue;
        attach(eager);
        play(eager);
      }
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        var video = entry.target.querySelector('video');
        if (!video) continue;

        if (entry.isIntersecting) {
          attach(video);
          play(video);
        } else {
          video.pause();
        }
      }
    }, { threshold: 0.15 });

    for (var p = 0; p < panels.length; p++) io.observe(panels[p]);
  }

  /* --- 4. work tiles ---------------------------------------------------- */

  function initWorkFilms() {
    if (reduceMotion.matches) return;

    /* A phone has no hover to give, so for as long as these were hover-only the films in
       the work tiles never played once on a touch device — the tile sat on its still for
       the whole visit and the little play mark was the only hint there was anything to
       see. There they behave like the panel films instead: play on the way in, pause on
       the way out. The tap still goes to the product; nothing here intercepts it.

       Two signals, not one. `hover: none` is the honest question but it is also the one
       that gets answered wrong — a touchscreen laptop says it can hover, some Android
       browsers say so regardless — so the theme's own mobile breakpoint counts too. The
       cost of a false positive is a film playing on a narrow desktop window, which is
       what the phone does anyway; the cost of a false negative is the bug being reported
       here. */
    var playOnView = !window.matchMedia('(hover: hover)').matches ||
                     window.matchMedia('(max-width: 749px)').matches;

    if (playOnView && hasIO) {
      var tiles = root.querySelectorAll('[data-work-film]');
      var tileIO = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var tile = entries[i].target;
          var video = tile.querySelector('video');
          if (!video) continue;

          if (entries[i].isIntersecting) {
            attach(video);
            play(video);
            tile.classList.add('is-playing');
          } else {
            video.pause();
            tile.classList.remove('is-playing');
          }
        }
        // Half on screen is enough: these sit in horizontal rails, so the tile being
        // looked at is the one in the middle, not the one clipped at the edge.
      }, { threshold: 0.5 });

      for (var w = 0; w < tiles.length; w++) tileIO.observe(tiles[w]);
      // The hover pair below would then fight the observer — pointerleave pausing a film
      // it wants running — so on these devices the observer is the whole behaviour.
      return;
    }

    // Delegated so tiles rendered by any later section still work, and so a rail of
    // twenty tiles costs two listeners rather than forty.
    root.addEventListener('pointerenter', function (event) {
      if (event.pointerType === 'touch') return;
      var tile = event.target.closest ? event.target.closest('[data-work-film]') : null;
      if (!tile) return;
      var video = tile.querySelector('video');
      if (!video) return;
      attach(video);
      play(video);
      tile.classList.add('is-playing');
    }, true);

    root.addEventListener('pointerleave', function (event) {
      var tile = event.target.closest ? event.target.closest('[data-work-film]') : null;
      if (!tile) return;
      var video = tile.querySelector('video');
      if (video) video.pause();
      tile.classList.remove('is-playing');
    }, true);
  }

  /* --- 5. rails --------------------------------------------------------- */

  function initRails() {
    var rails = root.querySelectorAll('[data-rail]');

    for (var i = 0; i < rails.length; i++) {
      (function (rail) {
        var group = rail.closest('[data-rail-group]');
        if (!group) return;

        var prev = group.querySelector('[data-rail-prev]');
        var next = group.querySelector('[data-rail-next]');
        if (!prev || !next) return;

        function step() {
          var first = rail.firstElementChild;
          if (!first) return rail.clientWidth;
          // One card plus its gap, so a click lands the next card flush at the edge.
          var gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
          return first.getBoundingClientRect().width + gap;
        }

        function sync() {
          // 2px of slack: sub-pixel widths mean scrollLeft rarely hits the exact maximum.
          var max = rail.scrollWidth - rail.clientWidth;
          prev.disabled = rail.scrollLeft <= 2;
          next.disabled = rail.scrollLeft >= max - 2;
        }

        prev.addEventListener('click', function () {
          rail.scrollBy({ left: -step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        });

        next.addEventListener('click', function () {
          rail.scrollBy({ left: step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        });

        rail.addEventListener('scroll', sync, { passive: true });
        window.addEventListener('resize', sync);
        sync();
      })(rails[i]);
    }
  }

  initReveal();
  initFilms();
  initWorkFilms();
  initRails();
})();
